import { useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

// ── Helpers ──

const isBooleanType = (type) => type === 'boolean' || type === 'boolean_with_comment';

const isClosingWithVisible = (fieldKey, preferences) => {
  const prefix = fieldKey.replace(/closing_with$/, '').replace(/_$/, '');
  const sideKey = prefix ? `${prefix}_closing_side` : 'closing_side';
  const sideValue = preferences[sideKey];
  return sideValue && sideValue !== 'na';
};

const isFieldVisible = (field, preferences) => {
  if (field.key.endsWith('closing_with') || field.key === 'closing_with') {
    return isClosingWithVisible(field.key, preferences);
  }
  return true;
};

// ── Compact Toggle Card (for boolean / boolean_with_comment) ──

function ToggleCard({ field, value, onChange }) {
  const currentValue = typeof value === 'object' ? value?.value || '' : (value || '');
  const currentComment = typeof value === 'object' ? value?.comment || '' : '';

  const handleSelect = (optKey) => {
    if (field.type === 'boolean_with_comment') {
      onChange({ value: optKey, comment: optKey === field.comment_condition ? currentComment : '' });
    } else {
      onChange(optKey);
    }
  };

  return (
    <div className={`border rounded-none p-3 transition-all ${currentValue ? 'border-ink/60' : 'border-border'}`} data-testid={`pref-${field.key}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium leading-tight">{field.label}</span>
        <div className="flex gap-1 flex-shrink-0">
          {field.options?.map((opt) => (
            <button
              key={opt.key}
              onClick={() => handleSelect(opt.key)}
              className={`px-3 py-1 text-[10px] uppercase tracking-luxe font-semibold font-medium border transition-all ${
                currentValue === opt.key
                  ? 'bg-ink text-background border-ink'
                  : 'bg-card border-border hover:border-ink/50'
              }`}
              data-testid={`pref-opt-${field.key}-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {field.type === 'boolean_with_comment' && currentValue === field.comment_condition && (
        <Input
          value={currentComment}
          onChange={(e) => onChange({ value: currentValue, comment: e.target.value })}
          placeholder={field.comment_label || 'Enter details...'}
          className="mt-2 text-xs h-8"
          data-testid={`pref-comment-${field.key}`}
        />
      )}
    </div>
  );
}

// ── Full-width Accordion Card (for complex types) ──

function PreferenceCard({ field, value, onChange, expanded, onToggle }) {
  const getSummary = () => {
    if (!value && value !== 0) return 'Not selected';
    if (Array.isArray(value)) return value.join(', ') || 'Not selected';
    if (typeof value === 'object' && value.values) return value.values.join(', ') + (value.comment ? ` (${value.comment})` : '');
    if (typeof value === 'string') return value || 'Not selected';
    return value?.value || 'Not selected';
  };

  const hasValue = () => {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value.values) return value.values.length > 0;
    if (typeof value === 'string') return value !== '';
    return !!value?.value;
  };

  return (
    <div className={`border rounded-none overflow-hidden transition-all ${hasValue() ? 'border-primary/30' : 'border-border'}`} data-testid={`pref-${field.key}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-card hover:bg-muted/50 transition-colors"
        data-testid={`pref-toggle-${field.key}`}
      >
        <div className="text-left flex items-center gap-2">
          {hasValue() && <Check className="w-3.5 h-3.5 text-ink flex-shrink-0" />}
          <div>
            <p className="text-sm font-medium">{field.label}</p>
            {!expanded && <p className="text-xs text-muted-foreground capitalize">{getSummary()}</p>}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-2">
          <CardInput field={field} value={value} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

// ── Card Input (for non-boolean types) ──

function CardInput({ field, value, onChange }) {
  if (field.type === 'text') {
    return (
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type here..."
        data-testid={`pref-text-${field.key}`}
      />
    );
  }

  if (field.type === 'multi_select') {
    const selected = Array.isArray(value) ? value : (value?.values || []);
    const comment = (!Array.isArray(value) && value?.comment) || '';

    const toggleOption = (optKey) => {
      const newSelected = selected.includes(optKey)
        ? selected.filter(k => k !== optKey)
        : [...selected, optKey];
      if (field.comment_condition && newSelected.includes(field.comment_condition)) {
        onChange(newSelected.length > 0 ? { values: newSelected, comment } : '');
      } else {
        onChange(newSelected.length > 0 ? newSelected : '');
      }
    };

    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {field.options?.map((opt) => (
            <button
              key={opt.key}
              onClick={() => toggleOption(opt.key)}
              className={`px-4 py-2 rounded-none text-sm font-medium border transition-all ${
                selected.includes(opt.key) ? 'bg-ink text-background border-ink' : 'bg-card border-border hover:border-ink/50'
              }`}
              data-testid={`pref-opt-${field.key}-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {field.comment_condition && selected.includes(field.comment_condition) && (
          <div>
            <Label className="text-xs">{field.comment_label || 'Please specify'}</Label>
            <Input value={comment} onChange={(e) => onChange({ values: selected, comment: e.target.value })} placeholder="Please specify..." className="mt-1" data-testid={`pref-comment-${field.key}`} />
          </div>
        )}
      </div>
    );
  }

  const currentValue = typeof value === 'string' ? value : value?.value || '';
  const currentComment = typeof value === 'object' ? value?.comment || '' : '';
  const currentCustomText = typeof value === 'object' ? value?.custom_text || '' : '';

  const handleOptionSelect = (optKey) => {
    if (field.type === 'single_select') onChange(optKey);
    else if (field.type === 'single_select_with_text') onChange({ value: optKey, custom_text: optKey === 'other' ? currentCustomText : '' });
    else if (field.type === 'conditional') onChange(optKey);
    else if (field.type === 'delivery_modifier') onChange(optKey);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {field.options?.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handleOptionSelect(opt.key)}
            className={`px-4 py-2 rounded-none text-sm font-medium border transition-all ${
              currentValue === opt.key ? 'bg-ink text-background border-ink' : 'bg-card border-border hover:border-ink/50'
            }`}
            data-testid={`pref-opt-${field.key}-${opt.key}`}
          >
            {opt.label}
            {field.type === 'delivery_modifier' && opt.key === 'express' && field.price_modifier > 0 && (
              <span className="text-xs ml-1 opacity-70">(+{field.price_modifier})</span>
            )}
          </button>
        ))}
      </div>
      {field.type === 'single_select_with_text' && currentValue === 'other' && (
        <div>
          <Label className="text-xs">Specify</Label>
          <Input value={currentCustomText} onChange={(e) => onChange({ value: 'other', custom_text: e.target.value })} placeholder="Please specify..." className="mt-1" data-testid={`pref-text-${field.key}`} />
        </div>
      )}
      {field.type === 'conditional' && field.children?.map((child) => {
        if (child.condition && child.condition.value !== currentValue) return null;
        return (
          <div key={child.key} className="ml-3 pl-3 border-l-2 border-primary/20">
            <p className="text-xs font-medium mb-2">{child.label}</p>
            <CardInput field={child} value={typeof value === 'object' ? value?.[child.key] : ''} onChange={(v) => onChange({ value: currentValue, [child.key]: v })} />
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ──

export default function PreferencesStep({ preferencesSchema, preferences, onChange }) {
  const [expanded, setExpanded] = useState(preferencesSchema[0]?.key || '');

  const handleReset = () => {
    onChange({});
    setExpanded(preferencesSchema[0]?.key || '');
  };

  // Count completed required fields
  const requiredFields = preferencesSchema.filter(f => f.required && isFieldVisible(f, preferences));
  const completedCount = requiredFields.filter(f => {
    const val = preferences[f.key];
    if (!val) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object' && val.values) return val.values.length > 0;
    if (typeof val === 'string') return val !== '';
    return !!val?.value;
  }).length;

  // Group fields by section, then split into cards vs toggles
  const renderFields = () => {
    const elements = [];
    let lastSection = '';
    let toggleBatch = [];

    const flushToggles = () => {
      if (toggleBatch.length === 0) return;
      elements.push(
        <div key={`toggles-${toggleBatch[0].key}`} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {toggleBatch.map(f => (
            <ToggleCard
              key={f.key}
              field={f}
              value={preferences[f.key]}
              onChange={(v) => onChange({ ...preferences, [f.key]: v })}
            />
          ))}
        </div>
      );
      toggleBatch = [];
    };

    for (const field of preferencesSchema) {
      if (!isFieldVisible(field, preferences)) continue;

      // Section header
      const sectionChanged = field.section && field.section !== lastSection;
      if (sectionChanged) {
        flushToggles();
        lastSection = field.section;
        elements.push(
          <div key={`section-${field.section}`} className="flex items-center gap-2 pt-4 pb-1" data-testid={`section-${field.section.toLowerCase()}`}>
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold uppercase tracking-wider text-ink">{field.section}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
        );
      }

      // Spacer before common tail fields (delivery_time, anything_else) that follow a section
      if (!field.section && lastSection && (field.key === 'delivery_time' || field.key === 'anything_else')) {
        if (field.key === 'delivery_time') {
          flushToggles();
          elements.push(<div key="common-divider" className="pt-4 mt-2 border-t border-border" />);
          lastSection = '';
        }
      }

      if (isBooleanType(field.type)) {
        toggleBatch.push(field);
      } else {
        flushToggles();
        elements.push(
          <PreferenceCard
            key={field.key}
            field={field}
            value={preferences[field.key]}
            onChange={(v) => onChange({ ...preferences, [field.key]: v })}
            expanded={expanded === field.key}
            onToggle={() => setExpanded(expanded === field.key ? '' : field.key)}
          />
        );
      }
    }
    flushToggles();
    return elements;
  };

  return (
    <div data-testid="preferences-step">
      <div className="flex items-start justify-between mb-8 gap-6">
        <div>
          <div className="uppercase tracking-luxe text-[11px] font-semibold text-gold mb-3">Step 02 of 03</div>
          <h2 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 'clamp(30px, 4vw, 44px)' }}>Tell us your preferences.</h2>
          <p className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mt-4">{completedCount} of {requiredFields.length} completed</p>
        </div>
        {Object.keys(preferences).length > 0 && (
          <button onClick={handleReset} className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted hover:text-destructive inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="preferences-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      <div className="space-y-3">
        {renderFields()}
      </div>
    </div>
  );
}
